import React, { useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, Search } from 'lucide-react';
import { SocRegimenGroup, Trial } from '../../types';
import { socRegimenGroupKey } from '../../utils/socRegimenGroups';
import SocReportFilters from './SocReportFilters';
import TrialTable from '../TrialTable';

interface SocRegimenViewProps {
  groups: SocRegimenGroup[];
  loading?: boolean;
  indicationName?: string;
  statusFilter: 'All' | Trial['preferredStatus'];
  onStatusFilterChange: (status: 'All' | Trial['preferredStatus']) => void;
}

const STATUS_STYLES: Record<Trial['preferredStatus'], string> = {
  Preferred: 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/20',
  'Other Recommended': 'bg-amber-50 text-amber-700 ring-1 ring-amber-600/20',
  'Useful in Certain Circumstances': 'bg-sky-50 text-sky-700 ring-1 ring-sky-600/20',
  'N/A': 'bg-slate-50 text-slate-500 ring-1 ring-slate-400/20',
};

function regimenSideBox(label: string, children: React.ReactNode, className = 'flex-1 min-w-0') {
  return (
    <div className={`${className} rounded-lg border border-slate-200 bg-slate-50/80 px-2.5 py-2`}>
      <p className="text-[9px] font-bold uppercase tracking-wide text-slate-400 mb-1">{label}</p>
      <div className="text-[11px] text-slate-700 leading-snug whitespace-pre-wrap break-words">{children}</div>
    </div>
  );
}

function pmidKey(trial: Trial, fallbackPmid?: string | null): string {
  const fromField = (trial.pmid || '').trim();
  if (fromField) return fromField;
  const fromFallback = (fallbackPmid || '').trim();
  if (fromFallback) return fromFallback;
  const fromId = String(trial.id || '').match(/(\d{5,})/);
  if (fromId) return fromId[1];
  return `id:${trial.id}`;
}

/** Collect unique trials for a regimen, keeping same-PMID arms contiguous. */
function trialsForGroup(group: SocRegimenGroup): Trial[] {
  const seen = new Set<string>();
  const byPmid = new Map<string, Trial[]>();
  const pmidOrder: string[] = [];

  for (const ref of group.references) {
    const rows = ref.trials?.length ? ref.trials : ref.trial ? [ref.trial] : [];
    for (const trial of rows) {
      if (seen.has(trial.id)) continue;
      seen.add(trial.id);
      const key = pmidKey(trial, ref.pmid);
      if (!byPmid.has(key)) {
        byPmid.set(key, []);
        pmidOrder.push(key);
      }
      byPmid.get(key)!.push(trial);
    }
  }

  return pmidOrder.flatMap((key) =>
    byPmid.get(key)!.sort((a, b) => String(a.id).localeCompare(String(b.id), undefined, { numeric: true })),
  );
}

function regimenHasTrialData(group: SocRegimenGroup): boolean {
  return group.references.some((ref) => {
    const rows = ref.trials?.length ? ref.trials : ref.trial ? [ref.trial] : [];
    return rows.length > 0;
  });
}

function referencesWithoutTrials(group: SocRegimenGroup) {
  return group.references.filter((ref) => {
    const rows = ref.trials?.length ? ref.trials : ref.trial ? [ref.trial] : [];
    return rows.length === 0;
  });
}

function RegimenReferencesPanel({ references }: { references: SocRegimenGroup['references'] }) {
  return (
    <div className="px-4 pb-4 space-y-2">
      <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400 px-1">
        NCCN mapping (no ruby_data trial row)
      </p>
      {references.map((ref, idx) => (
        <div
          key={`${ref.mapIdentifier || ref.pmid || idx}`}
          className="text-xs text-slate-600 p-3 bg-white rounded-lg border border-slate-200"
        >
          {ref.mapIdentifier && (
            <p className="mb-1">
              <span className="font-semibold text-slate-700">Identifier:</span> {ref.mapIdentifier}
            </p>
          )}
          {ref.refNum && (
            <p className="mb-1">
              <span className="font-semibold text-slate-700">NCCN ref #:</span> {ref.refNum}
            </p>
          )}
          {ref.refContents ? (
            <p className="whitespace-pre-wrap text-slate-600">{ref.refContents}</p>
          ) : (
            <p className="text-slate-500 italic">No reference text on file.</p>
          )}
        </div>
      ))}
    </div>
  );
}

const SocRegimenView: React.FC<SocRegimenViewProps> = ({
  groups,
  loading = false,
  indicationName,
  statusFilter,
  onStatusFilterChange,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedRegimens, setExpandedRegimens] = useState<string[]>([]);

  // Regimen groups are already NCCN-only from the API; keep null/"None" statuses visible.
  const filteredGroups = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return groups
      .filter((g) => statusFilter === 'All' || g.preferredStatus === statusFilter)
      .filter((g) => {
        if (!term) return true;
        const hay = [
          g.regimenName,
          g.nccnNotes,
          g.evidenceCategory,
          g.pmids.join(' '),
          ...g.references.flatMap((r) => {
            const rows = r.trials?.length ? r.trials : r.trial ? [r.trial] : [];
            return [r.mapIdentifier, r.pmid, r.refNum, ...rows.map((t) => [t.trialName, t.expDrug, t.id].join(' '))];
          }),
        ]
          .join(' ')
          .toLowerCase();
        return hay.includes(term);
      });
  }, [groups, statusFilter, searchTerm]);

  const toggleRegimen = (key: string) => {
    setExpandedRegimens((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  if (loading) {
    return (
      <div className="bg-white rounded-[2rem] shadow-card border border-gray-100 p-10 text-center text-sm text-slate-500">
        Loading NCCN regimens…
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-card border border-gray-100 overflow-hidden flex flex-col">
      <div className="px-4 sm:px-5 py-3 border-b border-gray-100 bg-white">
        <SocReportFilters
          statusFilter={statusFilter}
          onStatusFilterChange={onStatusFilterChange}
          guidelineFilter="nccn"
          onGuidelineFilterChange={() => {}}
          hideGuidelineFilter
        />
      </div>
      <div className="px-4 sm:px-5 py-3 border-b border-gray-100 flex flex-col sm:flex-row justify-between gap-3 items-center bg-white">
        <div>
          <h3 className="text-base font-semibold text-slate-900">NCCN regimens</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            {filteredGroups.length} of {groups.length} regimen{groups.length === 1 ? '' : 's'} · grouped from pmid_map
          </p>
        </div>
        <div className="relative group w-full sm:w-72">
          <input
            type="text"
            placeholder="Search regimens, notes…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 text-sm bg-slate-50 border-none ring-1 ring-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500/20 focus:bg-white outline-none w-full transition-all text-slate-600 placeholder:text-slate-400"
          />
          <Search className="absolute left-3.5 top-2.5 w-4 h-4 text-slate-400 group-focus-within:text-indigo-500 transition-colors" />
        </div>
      </div>

      <div className="p-3 sm:p-4 space-y-3 max-h-[75vh] overflow-y-auto custom-scrollbar">
        {groups.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-12">
            No NCCN regimen mappings found for this report. Restart the backend if you recently updated the app.
          </p>
        ) : filteredGroups.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-12">No regimens match the current filters.</p>
        ) : (
          filteredGroups.map((group) => {
            const regimenKey = socRegimenGroupKey(group);
            const isOpen = expandedRegimens.includes(regimenKey);
            const evidenceLabel = group.evidenceCategory?.trim() || 'Category 2A';
            const regimenTrials = trialsForGroup(group);
            const hasTrials = regimenHasTrialData(group);
            const refsWithoutTrials = referencesWithoutTrials(group);

            return (
              <div
                key={regimenKey}
                className="rounded-2xl border border-slate-200 overflow-hidden bg-white shadow-sm"
              >
                <button
                  type="button"
                  onClick={() => toggleRegimen(regimenKey)}
                  className="w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-slate-50/80 transition-colors"
                >
                  <div className="mt-0.5 shrink-0 text-slate-400">
                    {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-0.5">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_STYLES[group.preferredStatus]}`}
                      >
                        {group.preferredStatus}
                      </span>
                      <span className="text-xs text-slate-400">
                        {group.referenceCount} reference{group.referenceCount === 1 ? '' : 's'}
                      </span>
                      {!hasTrials && (
                        <span className="text-[10px] font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full ring-1 ring-amber-600/15">
                          Guideline only
                        </span>
                      )}
                    </div>
                    <h4 className="text-sm font-bold text-slate-900 break-words leading-snug">{group.regimenName}</h4>
                  </div>
                  <div className="hidden md:flex shrink-0 w-[min(62%,44rem)] gap-2 items-stretch">
                    {regimenSideBox(
                      'NCCN notes',
                      group.nccnNotes?.trim() ? (
                        group.nccnNotes
                      ) : (
                        <span className="text-slate-400">No notes</span>
                      ),
                      'flex-1 min-w-0',
                    )}
                    {regimenSideBox(
                      'Evidence category',
                      evidenceLabel,
                      'w-[8.5rem] shrink-0',
                    )}
                  </div>
                </button>
                <div className="md:hidden px-4 pb-3 flex gap-2 border-b border-slate-100">
                  {regimenSideBox(
                    'NCCN notes',
                    group.nccnNotes?.trim() ? (
                      group.nccnNotes
                    ) : (
                      <span className="text-slate-400">No notes</span>
                    ),
                  )}
                  {regimenSideBox(
                    'Evidence category',
                    evidenceLabel,
                  )}
                </div>

                {isOpen && (
                  <div className="border-t border-slate-100 bg-slate-50/40">
                    {regimenTrials.length > 0 && (
                      <TrialTable
                        compact
                        data={regimenTrials}
                        selectedIds={[]}
                        onToggleSelection={() => {}}
                        indicationName={indicationName}
                        statusFilter="All"
                        guidelineFilter="nccn"
                      />
                    )}
                    {!hasTrials && group.references.length > 0 && (
                      <RegimenReferencesPanel references={group.references} />
                    )}
                    {hasTrials && refsWithoutTrials.length > 0 && (
                      <div className="border-t border-slate-100">
                        <RegimenReferencesPanel references={refsWithoutTrials} />
                      </div>
                    )}
                    {group.references.length === 0 && (
                      <p className="px-4 py-6 text-sm text-slate-500 text-center">No references for this regimen.</p>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default SocRegimenView;
